import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Divider,
  Link,
  Tooltip,
  IconButton,
  Autocomplete,
  Chip,
} from '@mui/material';
import { DateField } from '../dropdown';
import { IconCopy, IconExternalLink } from '@tabler/icons-react';
import { FIELD_SX, PRIMARY_BTN_SX } from '../../styles/designSystem';
import {
  EditableCustomer,
  EditableCustomerUpdates,
  CustomerUpdateResult,
} from '../../services/customerUpdateApi';
import { getCustomerIndexCached } from '../../services/customerIndexCacheService';
import type { CustomerIndexItemDto } from '@techgitter/contracts';

const REPAIRSHOPR_BASE_URL = 'https://techsolvers.repairshopr.com';

interface CustomerEditFormProps {
  customer: EditableCustomer;
  isSaving: boolean;
  saveError: string | null;
  lastSaveResult: CustomerUpdateResult | null;
  onSave: (updates: EditableCustomerUpdates) => Promise<CustomerUpdateResult | void>;
  onCancel?: () => void;
  mode?: 'edit' | 'create';
  submitLabel?: string;
  title?: string;
  showIdentity?: boolean;
}

type FormState = Omit<EditableCustomer,
  'dataverseCustomerId' | 'repairShoprCustomerId' | 'createdAt' | 'updatedAt'
>;

const buildFormState = (customer: EditableCustomer): FormState => ({
  firstName: customer.firstName,
  lastName: customer.lastName,
  fullName: customer.fullName,
  businessName: customer.businessName,
  designation: customer.designation,
  email: customer.email,
  phone: customer.phone,
  mobile: customer.mobile,
  address: customer.address,
  address2: customer.address2,
  city: customer.city,
  state: customer.state,
  zip: customer.zip,
  notes: customer.notes,
  getSms: customer.getSms,
  optOut: customer.optOut,
  disabled: customer.disabled,
  noEmail: customer.noEmail,
  notificationEmail: customer.notificationEmail,
  invoiceCcEmails: customer.invoiceCcEmails,
  alias: customer.alias,
  domains: customer.domains,
  abn: customer.abn,
  relationshipTerminationDate: customer.relationshipTerminationDate,
  sendXeroInvoiceOnly: customer.sendXeroInvoiceOnly,
  xeroContactId: customer.xeroContactId,
  parentCustomerId: customer.parentCustomerId,
});

const diffUpdates = (original: FormState, current: FormState): EditableCustomerUpdates => {
  const updates: EditableCustomerUpdates = {};
  (Object.keys(current) as Array<keyof FormState>).forEach((key) => {
    if (current[key] !== original[key]) {
      (updates as Record<string, unknown>)[key] = current[key];
    }
  });
  return updates;
};

export const CustomerEditForm: React.FC<CustomerEditFormProps> = ({
  customer,
  isSaving,
  saveError,
  lastSaveResult,
  onSave,
  onCancel,
  mode = 'edit',
  submitLabel,
  title,
  showIdentity = true,
}) => {
  const [original, setOriginal] = useState<FormState>(() => buildFormState(customer));
  const [form, setForm] = useState<FormState>(() => buildFormState(customer));
  const [customers, setCustomers] = useState<CustomerIndexItemDto[]>([]);

  useEffect(() => {
    const state = buildFormState(customer);
    setOriginal(state);
    setForm(state);
  }, [customer]);

  // Customer index for the billing-group parent picker (edit mode only).
  useEffect(() => {
    if (mode !== 'edit') return;
    let active = true;
    getCustomerIndexCached()
      .then((res) => {
        if (active) setCustomers(res.items);
      })
      .catch(() => {
        /* picker stays empty; non-fatal */
      });
    return () => {
      active = false;
    };
  }, [mode]);

  const selectedParent =
    customers.find((c) => c.dataverseCustomerId === form.parentCustomerId) ?? null;

  const handleString = (key: keyof FormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const value = e.target.value;
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'firstName' || key === 'lastName') {
        next.fullName = `${next.firstName} ${next.lastName}`.trim();
      }
      return next;
    });
  };

  const handleBool = (key: keyof FormState) => (
    _e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    setForm((prev) => ({ ...prev, [key]: checked }));
  };

  const handleSubmit = async () => {
    if (mode === 'create') {
      try {
        await onSave(form as EditableCustomerUpdates);
      } catch {
        // surfaced via saveError
      }
      return;
    }
    const updates = diffUpdates(original, form);
    if (Object.keys(updates).length === 0) {
      return;
    }
    try {
      await onSave(updates);
    } catch {
      // Error surfaced via saveError prop
    }
  };

  const handleDiscard = () => {
    setForm(buildFormState(customer));
  };

  const hasChanges = Object.keys(diffUpdates(original, form)).length > 0;
  const resolvedTitle = title ?? (mode === 'create' ? 'New Customer' : 'Edit Customer');
  const resolvedSubmit = submitLabel ?? (mode === 'create' ? 'Create Customer' : 'Save');

  return (
    <Box sx={FIELD_SX}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a', mb: 1 }}>
        {resolvedTitle}
      </Typography>

      {showIdentity && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">DV ID:</Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
              {customer.dataverseCustomerId}
            </Typography>
            <Tooltip title="Copy Dataverse ID">
              <IconButton
                size="small"
                onClick={() => navigator.clipboard.writeText(customer.dataverseCustomerId)}
              >
                <IconCopy size={14} stroke={1.5} />
              </IconButton>
            </Tooltip>
          </Box>
          {customer.repairShoprCustomerId ? (
            <Link
              href={`${REPAIRSHOPR_BASE_URL}/customers/${customer.repairShoprCustomerId}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="caption"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              Open in RepairShopr (#{customer.repairShoprCustomerId})
              <IconExternalLink size={14} stroke={1.5} />
            </Link>
          ) : null}
        </Box>
      )}

      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {saveError}
        </Alert>
      )}

      {lastSaveResult && lastSaveResult.repairshopr_error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Dataverse updated but RepairShopr sync failed: {lastSaveResult.repairshopr_error}
        </Alert>
      )}

      <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>Name</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' }, gap: 2, mb: 2 }}>
        <TextField label="First Name" size="small" value={form.firstName} onChange={handleString('firstName')} sx={{ gridColumn: { sm: 'span 1' } }} />
        <TextField label="Last Name" size="small" value={form.lastName} onChange={handleString('lastName')} sx={{ gridColumn: { sm: 'span 1' } }} />
        <TextField label="Business Name" size="small" value={form.businessName} onChange={handleString('businessName')} sx={{ gridColumn: { sm: 'span 2' } }} />
        <TextField label="Full Name" size="small" value={form.fullName} onChange={handleString('fullName')} sx={{ gridColumn: { sm: 'span 2' } }} />
        <TextField label="ABN" size="small" value={form.abn} onChange={handleString('abn')} sx={{ gridColumn: { sm: 'span 1' } }} />
        <TextField label="Designation" size="small" value={form.designation} onChange={handleString('designation')} sx={{ gridColumn: { sm: 'span 1' } }} />
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>Contact</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' }, gap: 2, mb: 2 }}>
        <TextField label="Email" size="small" type="email" value={form.email} onChange={handleString('email')} sx={{ gridColumn: { sm: 'span 2' } }} />
        <TextField label="Phone" size="small" value={form.phone} onChange={handleString('phone')} sx={{ gridColumn: { sm: 'span 1' } }} />
        <TextField label="Mobile" size="small" value={form.mobile} onChange={handleString('mobile')} sx={{ gridColumn: { sm: 'span 1' } }} />
        <TextField label="Notification Email" size="small" value={form.notificationEmail} onChange={handleString('notificationEmail')} sx={{ gridColumn: { sm: 'span 2' } }} />
        <TextField label="Invoice CC Emails" size="small" value={form.invoiceCcEmails} onChange={handleString('invoiceCcEmails')} sx={{ gridColumn: { sm: 'span 2' } }} />
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>Address</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' }, gap: 2, mb: 2 }}>
        <Tooltip title="Address Line 1 (Street Address)">
          <TextField label="Address Line 1 (Street Address)" size="small" value={form.address} onChange={handleString('address')} sx={{ gridColumn: { sm: 'span 2' } }} />
        </Tooltip>
        <Tooltip title="Address Line 2 (Unit / Apartment / Floor etc)">
          <TextField label="Address Line 2 (Unit / Apartment / Floor etc)" size="small" value={form.address2} onChange={handleString('address2')} sx={{ gridColumn: { sm: 'span 2' } }} />
        </Tooltip>
        <TextField label="City" size="small" value={form.city} onChange={handleString('city')} sx={{ gridColumn: { sm: 'span 2' } }} />
        <TextField label="Postcode" size="small" value={form.zip} onChange={handleString('zip')} sx={{ gridColumn: { sm: 'span 1' } }} />
        <TextField label="State" size="small" value={form.state} onChange={handleString('state')} sx={{ gridColumn: { sm: 'span 1' } }} />
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>Other</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2, alignItems: 'start' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Autocomplete
            multiple
            freeSolo
            autoSelect
            size="small"
            options={[]}
            value={form.alias ? form.alias.split(',').map((a) => a.trim()).filter(Boolean) : []}
            onChange={(_e, next) => {
              const cleaned = (next as string[]).map((a) => a.trim()).filter(Boolean);
              setForm((prev) => ({ ...prev, alias: cleaned.join(',') }));
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return <Chip key={key} size="small" label={option} {...tagProps} />;
              })
            }
            renderInput={(params) => (
              <TextField {...params} label="Also known as / Related businesses" size="small" placeholder="Add alias" />
            )}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              label="Xero Contact ID"
              size="small"
              value={form.xeroContactId}
              onChange={handleString('xeroContactId')}
            />
            <DateField
              label="Relationship Termination Date"
              value={(form.relationshipTerminationDate || '').slice(0, 10) || null}
              onChange={(iso) => {
                setForm((prev) => ({ ...prev, relationshipTerminationDate: iso ? iso : null }));
              }}
            />
          </Box>
        </Box>
        <Autocomplete
          multiple
          freeSolo
          autoSelect
          size="small"
          options={[]}
          value={form.domains ? form.domains.split(',').map((d) => d.trim()).filter(Boolean) : []}
          onChange={(_e, next) => {
            const cleaned = (next as string[]).map((d) => d.trim()).filter(Boolean);
            setForm((prev) => ({ ...prev, domains: cleaned.join(',') }));
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return <Chip key={key} size="small" label={option} {...tagProps} />;
            })
          }
          renderInput={(params) => (
            <TextField {...params} label="Domains" size="small" placeholder="Add domain" />
          )}
        />
        {mode === 'edit' && (
          <Autocomplete
            size="small"
            options={customers.filter(
              (c) => c.dataverseCustomerId !== customer.dataverseCustomerId,
            )}
            value={selectedParent}
            getOptionLabel={(o) => o.displayName ?? ''}
            isOptionEqualToValue={(o, v) =>
              o.dataverseCustomerId === v.dataverseCustomerId
            }
            onChange={(_e, v) =>
              setForm((prev) => ({
                ...prev,
                parentCustomerId: v ? v.dataverseCustomerId : null,
              }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Parent / billing group"
                size="small"
                helperText="Reconcile this business's billing under a parent. Blank = standalone."
              />
            )}
            sx={{ gridColumn: { sm: 'span 2' } }}
          />
        )}
        <TextField
          label="Notes"
          size="small"
          value={form.notes}
          onChange={handleString('notes')}
          multiline
          minRows={3}
          sx={{ gridColumn: { sm: 'span 2' } }}
        />
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <FormControlLabel
          control={<Checkbox size="small" checked={form.getSms} onChange={handleBool('getSms')} />}
          label="SMS Enabled"
        />
        <FormControlLabel
          control={<Checkbox size="small" checked={form.optOut} onChange={handleBool('optOut')} />}
          label="Opt Out (Marketing)"
        />
        <FormControlLabel
          control={<Checkbox size="small" checked={form.noEmail} onChange={handleBool('noEmail')} />}
          label="No Email"
        />
        <FormControlLabel
          control={<Checkbox size="small" checked={form.disabled} onChange={handleBool('disabled')} />}
          label="Disabled"
        />
        <FormControlLabel
          control={<Checkbox size="small" checked={form.sendXeroInvoiceOnly} onChange={handleBool('sendXeroInvoiceOnly')} />}
          label="Send Xero Invoice Only"
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
        <Button
          variant="contained"
          startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClick={handleSubmit}
          disabled={isSaving || (mode === 'edit' && !hasChanges)}
          sx={PRIMARY_BTN_SX}
        >
          {isSaving ? 'Saving...' : resolvedSubmit}
        </Button>
        {mode === 'edit' && hasChanges && (
          <Button
            onClick={handleDiscard}
            disabled={isSaving}
            sx={{ color: '#aaaaaa', '&:hover': { color: '#555555', bgcolor: 'transparent' } }}
          >
            Discard
          </Button>
        )}
        {onCancel && (
          <Button
            onClick={onCancel}
            disabled={isSaving}
            sx={{ color: '#aaaaaa', '&:hover': { color: '#555555', bgcolor: 'transparent' } }}
          >
            Cancel
          </Button>
        )}
      </Box>
    </Box>
  );
};
